import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType } from '../types/auth';
import { authAPI, fetchMeShared } from '../services/api';
import { clearPersistedQueries } from '../lib/queryPersister';
import { readCachedMe, writeCachedMe, clearBootstrapCache } from '../utils/bootstrapCache';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      // Start each sign-in from a clean cache so a new user never sees the
      // previous user's persisted (localStorage) query/bootstrap data.
      clearPersistedQueries();
      clearBootstrapCache();
      const response = await authAPI.login({ email, password });
      setUser(response.user);
      writeCachedMe(response.user);
      setCsrfToken(response.csrf_token);
      setAuthChecked(true);
      // Return whether user must change password
      return response.must_change_password || false;
    } catch (error) {
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
    } finally {
      setUser(null);
      setCsrfToken(null);
      setAuthChecked(true);
      // Wipe both in-memory and persisted cache so nothing lingers for the
      // next user of this browser.
      clearPersistedQueries();
      clearBootstrapCache();
    }
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admin has all roles
    return user.role === role;
  };


  useEffect(() => {
    let mounted = true;
    
    const performAuthCheck = async () => {
      if (!mounted || authChecked) return;

      // Paint instantly from the cached user (revalidated below). The server
      // still gates every protected endpoint by cookie, so a stale cached user
      // can't access anything — at worst the app flashes then redirects to login.
      const cachedUser = readCachedMe();
      if (cachedUser && mounted) {
        setUser(cachedUser);
        setLoading(false);
      }

      try {
        // Always revalidate; fetchMeShared dedupes with PermissionContext.
        const currentUser = await fetchMeShared();
        if (mounted) {
          setUser(currentUser);
          writeCachedMe(currentUser);

          // Get CSRF token if we don't have one
          if (!csrfToken) {
            try {
              const token = await authAPI.getCsrfToken();
              if (mounted) {
                setCsrfToken(token);
              }
            } catch (csrfError) {
              console.warn('Échec de récupération du token CSRF:', csrfError);
            }
          }
          setAuthChecked(true);
        }
      } catch (error: any) {
        if (mounted) {
          console.error('Échec de vérification d\'authentification:', error);
          setUser(null);
          setCsrfToken(null);
          setAuthChecked(true);
          // Session is no longer valid — drop the stale cached user/permissions.
          clearBootstrapCache();
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    performAuthCheck();
    
    // Cleanup function
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - dependencies intentionally excluded to prevent loops

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    hasRole,
    csrfToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};